import { useMemo, useState, useEffect, useRef } from 'react';
import { useShouldShowIAFeature } from 'hooks/IAFeaturesHook';
import Organization from 'infrastructure/Organization';
import { useGetFF } from 'hooks/useGetFF';

export const useShouldShowChatAssistRedirect = () => {
  const org_id = Organization.getSelectedOrg()?.slug;
  const { isEnabled, value, loading } = useGetFF({ flag_id: 'sales_assist' });
  const ai_integration = Organization.getSelectedOrg()?.ai_integration;
  const version = value?.version ?? 1;

  const enabled = useMemo(() => {
    if (isEnabled && ai_integration && value !== '') {
      if (!value?.checkBySlug) return true;
      return value?.slugs?.includes(org_id) ?? false;
    }
    return false;
  }, [isEnabled, ai_integration, org_id, value]);

  return { loading, enabled, version };
};

export const useShouldShowChatAssist = () => {
  const enabled = useShouldShowIAFeature('sales_assist');

  const routeItem = useMemo(() => {
    return Organization.isRep()
      ? { path: 'CHAT_REP', label: 'Kai' }
      : { path: 'CHAT', label: 'Nea' };
  }, []);

  return { enabled, routeItem };
};

export const useWhichLabelsToShow = () => {
  const chatAgent = useMemo(() => {
    return Organization.isRep() ? 'Kai' : 'Nea';
  }, []);

  return chatAgent;
};

export const useLiveTyping = ({ speed = 500 }) => {
  const [displayedLines, setDisplayedLines] = useState([]);
  const [isTyping, setIsTyping] = useState(false);

  const bufferRef = useRef([]);
  const intervalRef = useRef(null);

  const tableBufferRef = useRef([]);
  const isInTable = useRef(false);

  const appendChunk = (data) => {
    const lines = data.split('\n');

    bufferRef.current.push(...lines);
    if (!intervalRef.current) startTyping();
  };

  const startTyping = () => {
    setIsTyping(true);
    intervalRef.current = setInterval(() => {
      const nextLine = bufferRef.current.shift();

      if (nextLine === undefined) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setIsTyping(false);
        return;
      }

      const isTableLine = /^\s*\|.*\|\s*$/.test(nextLine);

      if (isTableLine) {
        isInTable.current = true;
        tableBufferRef.current.push(nextLine.trim());
      } else {
        if (isInTable.current && tableBufferRef.current.length) {
          // Fin de tabla: enviar toda junta
          setDisplayedLines((prev) => [...prev, tableBufferRef.current.join('\n')]);
          tableBufferRef.current = [];
          isInTable.current = false;
        }

        if (nextLine.trim() !== '') {
          setDisplayedLines((prev) => [...prev, nextLine]);
        }
      }
    }, speed);
  };

  const reset = () => {
    bufferRef.current = [];
    setDisplayedLines([]);
    setIsTyping(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return { displayedLines, isTyping, appendChunk, reset };
};
